import { useMemo, useState } from 'react';

import type { ProductItemCondition, ProductStatus } from '@/shared/api/types/generated';
import { ProductItemData } from '@/shared/types/product';

import type { ProductFilters } from '../types';

interface UseProductFeedDataParams {
  allProducts: ProductItemData[];
  status: ProductStatus;
}

interface UseProductFeedDataReturn {
  products: ProductItemData[];
  availableBrands: { brand: string; count: number }[];
  availableConditions: ProductItemCondition[];
  filters: ProductFilters;
  setFilters: (filters: ProductFilters) => void;
}

export const useProductFeedData = ({ allProducts, status }: UseProductFeedDataParams): UseProductFeedDataReturn => {
  const [filters, setFilters] = useState<ProductFilters>({
    sort: 'newest', // Par défaut: plus récent
  });

  // Filtrer et trier côté client
  const filteredProducts = useMemo(() => {
    let productsData = [...allProducts];

    // Filtrer par brand
    if (filters.brand) {
      productsData = productsData.filter((p) => p.brand === filters.brand);
    }

    // Filtrer par itemCondition
    if (filters.itemCondition) {
      productsData = productsData.filter((p) => p.itemCondition === filters.itemCondition);
    }

    // Filtre de prix
    if (filters.priceMin !== undefined) {
      productsData = productsData.filter((p) => (p.price ?? 0) >= filters.priceMin!);
    }
    if (filters.priceMax !== undefined) {
      productsData = productsData.filter((p) => (p.price ?? 0) <= filters.priceMax!);
    }

    // Filtre de favoris
    if (filters.favoritesMin !== undefined) {
      productsData = productsData.filter((p) => (p.favouriteCount ?? 0) >= filters.favoritesMin!);
    }
    if (filters.favoritesMax !== undefined) {
      productsData = productsData.filter((p) => (p.favouriteCount ?? 0) <= filters.favoritesMax!);
    }

    // Trier
    if (filters.sort) {
      productsData = productsData.sort((a, b) => {
        switch (filters.sort) {
          case 'newest':
            return (
              new Date(status === 'sold' ? (b.soldAt ?? 0) : (b.uploadedAt ?? 0)).getTime() -
              new Date(status === 'sold' ? (a.soldAt ?? 0) : (a.uploadedAt ?? 0)).getTime()
            );
          case 'oldest':
            return (
              new Date(status === 'sold' ? (a.soldAt ?? 0) : (a.uploadedAt ?? 0)).getTime() -
              new Date(status === 'sold' ? (b.soldAt ?? 0) : (b.uploadedAt ?? 0)).getTime()
            );
          case 'priceAsc':
            return (a.price ?? 0) - (b.price ?? 0);
          case 'priceDesc':
            return (b.price ?? 0) - (a.price ?? 0);
          default:
            return 0;
        }
      });
    }

    return productsData;
  }, [allProducts, filters, status]);

  // Extraire les filtres disponibles à partir des produits filtrés (sans le filtre correspondant)
  // Pour availableBrands : calculer à partir des produits filtrés sauf par brand
  const availableBrands = useMemo(() => {
    let productsForBrands = [...allProducts];

    // Appliquer tous les filtres sauf brand
    if (filters.itemCondition) {
      productsForBrands = productsForBrands.filter((p) => p.itemCondition === filters.itemCondition);
    }
    if (filters.priceMin !== undefined) {
      productsForBrands = productsForBrands.filter((p) => (p.price ?? 0) >= filters.priceMin!);
    }
    if (filters.priceMax !== undefined) {
      productsForBrands = productsForBrands.filter((p) => (p.price ?? 0) <= filters.priceMax!);
    }
    if (filters.favoritesMin !== undefined) {
      productsForBrands = productsForBrands.filter((p) => (p.favouriteCount ?? 0) >= filters.favoritesMin!);
    }
    if (filters.favoritesMax !== undefined) {
      productsForBrands = productsForBrands.filter((p) => (p.favouriteCount ?? 0) <= filters.favoritesMax!);
    }

    const brandCounts = new Map<string, number>();
    productsForBrands.forEach((product) => {
      if (product.brand) {
        brandCounts.set(product.brand, (brandCounts.get(product.brand) ?? 0) + 1);
      }
    });
    return Array.from(brandCounts.entries())
      .map(([brand, count]) => ({ brand, count }))
      .sort((a, b) => a.brand.localeCompare(b.brand));
  }, [
    allProducts,
    filters.itemCondition,
    filters.priceMin,
    filters.priceMax,
    filters.favoritesMin,
    filters.favoritesMax,
  ]);

  // Pour availableConditions : calculer à partir des produits filtrés sauf par itemCondition
  const availableConditions = useMemo(() => {
    let productsForConditions = [...allProducts];

    // Appliquer tous les filtres sauf itemCondition
    if (filters.brand) {
      productsForConditions = productsForConditions.filter((p) => p.brand === filters.brand);
    }
    if (filters.priceMin !== undefined) {
      productsForConditions = productsForConditions.filter((p) => (p.price ?? 0) >= filters.priceMin!);
    }
    if (filters.priceMax !== undefined) {
      productsForConditions = productsForConditions.filter((p) => (p.price ?? 0) <= filters.priceMax!);
    }
    if (filters.favoritesMin !== undefined) {
      productsForConditions = productsForConditions.filter((p) => (p.favouriteCount ?? 0) >= filters.favoritesMin!);
    }
    if (filters.favoritesMax !== undefined) {
      productsForConditions = productsForConditions.filter((p) => (p.favouriteCount ?? 0) <= filters.favoritesMax!);
    }

    const conditions = new Set<ProductItemCondition>();
    productsForConditions.forEach((product) => {
      if (product.itemCondition) {
        conditions.add(product.itemCondition);
      }
    });
    return Array.from(conditions).sort();
  }, [allProducts, filters.brand, filters.priceMin, filters.priceMax, filters.favoritesMin, filters.favoritesMax]);

  return {
    products: filteredProducts,
    availableBrands,
    availableConditions,
    filters,
    setFilters,
  };
};
